import React, {Component, ReactElement, RefObject} from 'react'
import {Box} from '@mui/material'
import withStyles from '@mui/styles/withStyles'
import {Colors, FontSize, FontWeights, LineHeight} from '../../../styledComponents/Constants'
import {Messages} from '../../../Messages'
import {LeftRowFlexContainer} from '../../../styledComponents/FlexComponents'
import {UnstyledLinkStyledButton} from '../../../styledComponents/Button'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faExternalLinkAlt} from '@fortawesome/free-solid-svg-icons'
import {Nullable} from '../../../app/TypeHelpers'
import styled from 'styled-components'
import {Phone} from '@mui/icons-material'
import {currentMedia} from '../../../styledComponents/ResponsiveConvenience'
import {StyledPageTitle} from '../../../components/reservations/components/CommonStyle'
import {HKConstants} from '../../../HKConstants'
import {getQueryParameter} from '../../common/util/Natutil'
import {NatOwnershipTransfersRepo} from '../ownershipTransfers/repository/NatOwnershipTransfersRepo'
import {
    createGuestWithErrorHandlingPage,
    GuestWithErrorHandlingPagePropsSet,
} from '../../../components/shared/hoc/createGuestWithErrorHandlingPage'
import {Location} from 'react-router-dom'

interface Props {
    location: Location
    ownershipTransfersRepo: NatOwnershipTransfersRepo
}

interface States {
    familyName: string,
    firstName: string,
    condominiumName: string,
    zipCodeFirst: string,
    zipCodeLatter: string,
    prefectures: string,
    address: string,
    familyNameKana: string,
    firstNameKana: string,
    mailAddress: string,
    manualUrl: Nullable<string>
    isLoadCompleted: boolean
}

class NatGuestContactusPage extends Component<Props & GuestWithErrorHandlingPagePropsSet, States> {
    submit: RefObject<HTMLFormElement>

    constructor(props: Props & GuestWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            familyName: ``,
            firstName: ``,
            condominiumName: ``,
            zipCodeFirst: ``,
            zipCodeLatter: ``,
            prefectures: ``,
            address: ``,
            familyNameKana: ``,
            firstNameKana: ``,
            mailAddress: ``,
            manualUrl: null,
            isLoadCompleted: false,
        }
        this.submit = React.createRef()
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const manualUrl = await this.getOwnershipTransferManualUrl()
            this.setState({manualUrl: manualUrl})
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    async getOwnershipTransferManualUrl(): Promise<Nullable<string>> {
        const {ownershipTransfersRepo, eventEmitter} = this.props
        const accessKey = getQueryParameter('accessKey', this.props.location.search)

        const response = await this.props.asyncCallFunction(
            ownershipTransfersRepo.getOwnershipTransferManualUrl.bind(ownershipTransfersRepo, accessKey),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response?.details?.manualUrl : null
    }

    renderManualLink(): ReactElement {
        const manualUrl = this.state.manualUrl
        if (!manualUrl) {
            return <></>
        }
        return (
            <>
                <Box height={'20px'}/>
                <ManualLink href={manualUrl} target={'_blank'}>
                    マニュアル
                </ManualLink>
                を掲載していますので、お問い合わせの際はまずこちらをご確認ください。
            </>
        )
    }

    // お問い合わせ（素敵ネット）
    contactusApartLinkClick(): void {
        window.open(HKConstants.VRS.SYS.CONTACT_WEB_URL_MANSION, '_blank')
    }

    // マンション管理全般について
    renderMansionContactus(): ReactElement {
        return (
            <>
                <Box height={'20px'}/>
                <Box fontSize={FontSize.Middle}>
                    長谷工コンタクトセンター
                </Box>
                <Box height={'5px'}/>
                <LeftRowFlexContainer>
                    <PhoneIcon fontSize={'large'}/>
                    <Box width={'10px'}/>
                    <Box fontSize={FontSize.Accent} fontWeight={FontWeights.Bold} color={Colors.AccentDark}>
                        {Messages.IRESR0007.toString()}
                    </Box>
                </LeftRowFlexContainer>
                <AlignItemsStartFlexContainer>
                    <Box>受付時間</Box>
                    <Box width={'10px'}/>
                    <NewLineContents>{Messages.IRESR0014.toString()}</NewLineContents>
                </AlignItemsStartFlexContainer>
                <Box height={'15px'}/>
                <SmallGuestLinkStyledButton onClick={this.contactusApartLinkClick} type={'button'}>
                    <LeftRowFlexContainer>
                        <FontAwesomeIcon icon={faExternalLinkAlt}/>
                        <Box width={'10px'}/>
                        お問い合わせフォーム
                    </LeftRowFlexContainer>
                </SmallGuestLinkStyledButton>
            </>
        )
    }

    render(): Nullable<ReactElement> {
        return (
            <>
                <form ref={this.submit} action={HKConstants.VRS.SYS.CONTACT_WEB_URL_SUTEKI} method={`post`}
                      target="_blank">
                    <Box height={'30px'}/>
                    <PageTitle>
                        お問い合わせ
                    </PageTitle>
                    {this.renderManualLink()}
                    <Box height={'20px'}/>
                    <WideWhiteArea>
                        <Box height={'15px'}/>
                        <Box>
                            マンション管理全般について
                        </Box>
                        {this.renderMansionContactus()}
                        <Box height={'20px'}/>
                    </WideWhiteArea>
                </form>
            </>
        )
    }
}

export default createGuestWithErrorHandlingPage(NatGuestContactusPage)

const ManualLink = styled.a`
  color: ${Colors.GuestPrimary};
  font-Size: ${FontSize.Body};
  line-Height: 24px;
}

&:hover {
  cursor: pointer;
}
`

const SmallGuestLinkStyledButton = styled(UnstyledLinkStyledButton)`
  color: ${Colors.GuestPrimary};
  margin: 0;
  display: inline-block;
  font-size: ${FontSize.Body};
  line-height: ${LineHeight.Basic};
  text-align: left;
`

const AlignItemsStartFlexContainer = styled(LeftRowFlexContainer)`
  align-items: flex-start;
  font-Size: ${FontSize.Small};
`

const PhoneIcon = withStyles({
    root: {
        color: Colors.GuestPrimary,
    },
})(Phone)

const WideWhiteArea = styled.div`
  background-color: ${Colors.Surface};
  ${currentMedia.lessThan('sp')`
    width: calc(100% + 30px);
    margin: 0px -15px;
  `};
  padding: 0 15px;
  box-sizing: border-box;
  overflow: hidden;
`
const PageTitle = styled(StyledPageTitle)`
  line-height: 1;
`

const NewLineContents = styled.div`
  ${currentMedia.lessThan('sp')`
      white-space:pre;
  `};
`
